import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { getGraphQLClient, UPDATE_USER_PROFILE, GET_ME } from '../lib/graphql';
import { Button, Input, Spinner } from '../components/common';
import { User } from '../types';
import McpIntegrationPanel from '../components/profile/McpIntegrationPanel';

const GENDER_OPTIONS = [
  { value: '', label: 'profile.selectGender' },
  { value: 'male', label: 'profile.genderMale' },
  { value: 'female', label: 'profile.genderFemale' },
  { value: 'non_binary', label: 'profile.genderNonBinary' },
  { value: 'prefer_not_to_say', label: 'profile.genderPreferNotToSay' },
];

const AI_MODEL_OPTIONS = [
  { value: '', label: 'profile.aiModelDefault' },
  { value: 'gemini-2.5-flash-lite', label: 'profile.aiModelFlashLite' },
  { value: 'gemini-2.5-flash', label: 'profile.aiModelFlash' },
  { value: 'gemini-2.5-pro', label: 'profile.aiModelPro' },
];

export default function ProfilePage() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const [name, setName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [gender, setGender] = useState('');
  const [biography, setBiography] = useState('');
  const [occupation, setOccupation] = useState('');
  const [interests, setInterests] = useState('');
  const [expertiseAreas, setExpertiseAreas] = useState('');
  const [preferredWritingStyle, setPreferredWritingStyle] = useState('');
  const [preferredAiModel, setPreferredAiModel] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const client = getGraphQLClient();
      const data = await client.request<{ me: User }>(GET_ME);
      const profile = data.me;

      setName(profile.name || '');
      setBirthday(profile.birthday || '');
      setGender(profile.gender || '');
      setBiography(profile.biography || '');
      setOccupation(profile.occupation || '');
      setInterests(profile.interests?.join(', ') || '');
      setExpertiseAreas(profile.expertiseAreas?.join(', ') || '');
      setPreferredWritingStyle(profile.preferredWritingStyle || '');
      setPreferredAiModel(profile.preferredAiModel || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrors([]);
    setSuccess(false);

    try {
      const client = getGraphQLClient();
      const variables = {
        name: name || null,
        birthday: birthday || null,
        gender: gender || null,
        biography: biography || null,
        occupation: occupation || null,
        interests: interests
          ? interests
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
        expertiseAreas: expertiseAreas
          ? expertiseAreas
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
        preferredWritingStyle: preferredWritingStyle || null,
        preferredAiModel: preferredAiModel || null,
      };

      const data = await client.request<{
        updateUserProfile: { user: User; errors: string[] };
      }>(UPDATE_USER_PROFILE, variables);

      if (data.updateUserProfile.errors.length > 0) {
        setErrors(data.updateUserProfile.errors);
      } else {
        setSuccess(true);
        if (data.updateUserProfile.user) {
          await refreshUser();
        }
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      setErrors([t('common.error')]);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">
        {t('profile.title')}
      </h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{t('profile.hint')}</p>

        <div className="space-y-6">
          <Input
            label={t('profile.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('profile.namePlaceholder')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('profile.birthday')}
              </label>
              <input
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('profile.gender')}
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {GENDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Input
            label={t('profile.occupation')}
            value={occupation}
            onChange={(e) => setOccupation(e.target.value)}
            placeholder={t('profile.occupationPlaceholder')}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('profile.biography')}
            </label>
            <textarea
              value={biography}
              onChange={(e) => setBiography(e.target.value)}
              placeholder={t('profile.biographyPlaceholder')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          <Input
            label={t('profile.interests')}
            value={interests}
            onChange={(e) => setInterests(e.target.value)}
            placeholder={t('profile.interestsPlaceholder')}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4">
            {t('profile.commaSeparated')}
          </p>

          <Input
            label={t('profile.expertiseAreas')}
            value={expertiseAreas}
            onChange={(e) => setExpertiseAreas(e.target.value)}
            placeholder={t('profile.expertiseAreasPlaceholder')}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4">
            {t('profile.commaSeparated')}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('profile.preferredWritingStyle')}
            </label>
            <textarea
              value={preferredWritingStyle}
              onChange={(e) => setPreferredWritingStyle(e.target.value)}
              placeholder={t('profile.preferredWritingStylePlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('profile.preferredWritingStyleHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('profile.preferredAiModel')}
            </label>
            <select
              value={preferredAiModel}
              onChange={(e) => setPreferredAiModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {AI_MODEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('profile.preferredAiModelHint')}
            </p>
          </div>

          {errors.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg"
            >
              {errors.map((error, idx) => (
                <p key={idx} className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              ))}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-3 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg"
            >
              <p className="text-sm text-green-600 dark:text-green-400">{t('profile.saved')}</p>
            </motion.div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleSave} isLoading={isSaving}>
              {t('profile.save')}
            </Button>
          </div>
        </div>
      </motion.div>

      <McpIntegrationPanel />
    </div>
  );
}
